import React from 'react';
import { Dialog, DialogTitle, DialogContent, IconButton, Typography, Box, Button } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

export interface PdfPreviewFile {
  name: string;
  url: string;
}

interface PdfPreviewDialogProps {
  open: boolean;
  file?: PdfPreviewFile;
  onClose: () => void;
  onRemove?: () => void;
  removable?: boolean;
}

const PdfPreviewDialog: React.FC<PdfPreviewDialogProps> = ({
  open,
  file,
  onClose,
  onRemove,
  removable = true,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: 1200,
          mx: { xs: 1.5, sm: 4 },
          my: { xs: 1.5, sm: 4 },
          height: { xs: 'calc(100vh - 24px)', sm: 'calc(100vh - 64px)' },
          borderRadius: 3,
          display: 'flex',
          flexDirection: 'column',
          boxShadow: (theme) => theme.shadows[24],
          overflow: 'hidden',
          p: 0,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pr: 1,
          py: 1.25,
          pl: 2.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6" fontWeight={700} sx={{ mr: 2, flex: 1, minWidth: 0 }}>
          {file?.name || 'Preview'}
        </Typography>
        <IconButton color="error" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          p: 0,
          flex: 1,
          overflow: 'hidden',
          display: 'flex',
          bgcolor: 'background.default',
          '& > object': { flex: 1 },
        }}
      >
        {file ? (
          <object data={file.url} type="application/pdf" width="100%" height="100%">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                fontSize: 14,
                color: 'text.secondary',
                p: 4,
                textAlign: 'center',
              }}
            >
              PDF cannot be displayed in this browser.
            </Box>
          </object>
        ) : null}
      </DialogContent>
      <Box
        sx={{
          p: 1.5,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        {removable && onRemove && (
          <Button
            variant="outlined"
            color="error"
            onClick={onRemove}
            size="small"
            sx={{ textTransform: 'none' }}
          >
            Remove
          </Button>
        )}
        <Button
          variant="contained"
            onClick={onClose}
            size="small"
            sx={{ textTransform: 'none' }}
        >
          Close
        </Button>
      </Box>
    </Dialog>
  );
};

export default PdfPreviewDialog;